import { observer } from "mobx-react-lite";
import { useState } from "react";
import styled from "styled-components";

import { useStores } from "../../stores/useStores";
import { color } from "../../theme/color";
import { Choice, FieldLabel } from "../ui/controls";
import { Dialog } from "../ui/Dialog";
import { TextField } from "../ui/TextField";

/** The id that stands for a new setlist in the choices. */
const NEW = "new";

/** Adds a version of a song to an existing setlist, or to a new one. */
export const AddToSetlistDialog = observer(function AddToSetlistDialog({ songId, onClose }: { songId: string; onClose(): void }) {
    const { library } = useStores();
    const versions = library.versionsOf(songId);
    const [chordSheetId, setChordSheetId] = useState(versions[0].id);
    const [setlistId, setSetlistId] = useState(library.setlists[0]?.id ?? NEW);
    const [name, setName] = useState("");
    const choices = [...library.setlists.map((setlist) => ({ id: setlist.id, label: setlist.name })), { id: NEW, label: "A new setlist" }];

    async function add() {
        await library.change(async (l) => {
            const target = setlistId === NEW ? (await l.createSetlist(name.trim())).id : setlistId;

            return l.addToSetlist(target, chordSheetId);
        });

        onClose();
    }

    return (
        <Dialog title="Add to a setlist" submit="Add" canSubmit={setlistId !== NEW || !!name.trim()} onSubmit={add} onClose={onClose}>
            {versions.length > 1 && (
                <FieldLabel>
                    Version
                    <Select value={chordSheetId} onChange={(event) => setChordSheetId(event.target.value)}>
                        {versions.map((sheet) => (
                            <option key={sheet.id} value={sheet.id}>
                                {library.title(sheet.id)}
                            </option>
                        ))}
                    </Select>
                </FieldLabel>
            )}
            {choices.map((choice) => (
                <Choice key={choice.id}>
                    <input type="radio" name="setlist" checked={setlistId === choice.id} onChange={() => setSetlistId(choice.id)} />
                    {choice.label}
                </Choice>
            ))}
            {setlistId === NEW && (
                <FieldLabel>
                    Name of the new setlist
                    <TextField autoFocus placeholder="Like Sunday 1 November" value={name} onChange={setName} />
                </FieldLabel>
            )}
        </Dialog>
    );
});

const Select = styled.select`
    height: 44px;
    padding: 0 10px;
    border: 1px solid ${color.borderStrong};
    border-radius: 10px;
    background: ${color.background};
    font-size: 16px;
`;
